/**
 * YouTube Service
 * Normalizes various YouTube link formats into consistent IDs, watch URLs, and embed URLs.
 */

export interface YouTubeInfo {
  youtubeId: string | null;
  watchUrl: string | null;
  embedUrl: string | null;
}

/**
 * Extracts the 11-character YouTube video ID from various input formats.
 */
export function extractYoutubeId(input: string): string | null {
  if (!input) return null;
  
  // 1. Check if it's already just an ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(input.trim())) {
    return input.trim();
  }
  
  // 2. Try parsing as a URL
  try {
    const url = new URL(input.trim());
    
    // youtu.be/ID
    if (url.hostname === 'youtu.be') {
      return url.pathname.slice(1).split(/[?#]/)[0];
    }
    
    // youtube.com/...
    if (url.hostname.includes('youtube.com')) {
      // /shorts/ID
      if (url.pathname.includes('/shorts/')) {
        return url.pathname.split('/shorts/')[1].split(/[?#]/)[0];
      }
      // /embed/ID
      if (url.pathname.includes('/embed/')) {
        return url.pathname.split('/embed/')[1].split(/[?#]/)[0];
      }
      // /watch?v=ID
      return url.searchParams.get('v');
    }
  } catch (e) {
    // Fallback to regex if URL constructor fails
    const match = input.match(/(?:shorts\/|v=|be\/|embed\/|^)([a-zA-Z0-9_-]{11})(?:[?&]|$)/);
    return match ? match[1] : null;
  }
  
  return null;
}

/**
 * Normalizes any YouTube input into a standard info object.
 */
export function normalizeYouTube(input: string): YouTubeInfo {
  const id = extractYoutubeId(input);
  
  if (!id) {
    return {
      youtubeId: null,
      watchUrl: null,
      embedUrl: null
    };
  }
  
  return {
    youtubeId: id,
    watchUrl: `https://www.youtube.com/watch?v=${id}`,
    embedUrl: `https://www.youtube.com/embed/${id}`
  };
}
